/**
 * Newsletter Popup
 * Shows a popup to encourage newsletter subscription on homepage
 */

class NewsletterPopup {
  constructor() {
    this.popup = document.getElementById('newsletter-popup');
    if (!this.popup) return;

    this.overlay = this.popup.querySelector('.newsletter-popup__overlay');
    this.closeBtn = this.popup.querySelector('.newsletter-popup__close');
    this.declineBtn = this.popup.querySelector('.newsletter-popup__decline');
    this.container = this.popup.querySelector('.newsletter-popup__container');

    this.storageKey = 'newsletter_popup_dismissed';
    this.showDelay = parseInt(this.popup.dataset.delay, 10) || 3000; // Read from data attribute or default 3s
    this.dismissDuration = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

    this.init();
  }

  init() {
    // Check if popup should be shown
    if (this.shouldShowPopup()) {
      setTimeout(() => this.show(), this.showDelay);
    }

    // Bind event listeners
    this.bindEvents();
  }

  shouldShowPopup() {
    // Don't show in Shopify editor
    if (window.Shopify && window.Shopify.designMode) {
      return false;
    }

    // Check if user has dismissed the popup recently
    const dismissedData = localStorage.getItem(this.storageKey);
    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData);
        const now = Date.now();
        if (now - timestamp < this.dismissDuration) {
          return false;
        }
      } catch (e) {
        // Invalid data, clear it
        localStorage.removeItem(this.storageKey);
      }
    }

    // Check if form was already submitted successfully
    const successMessage = this.popup.querySelector('.newsletter-popup__success');
    if (successMessage) {
      // Auto-close after showing success message
      setTimeout(() => this.close(), 3000);
      return true;
    }

    return true;
  }

  bindEvents() {
    // Close button click
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Decline button click
    if (this.declineBtn) {
      this.declineBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Prevent clicks inside container from closing
    if (this.container) {
      this.container.addEventListener('click', (e) => e.stopPropagation());
    }
  }

  show() {
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('newsletter-popup-open');

    // Focus on email input for accessibility
    const emailInput = this.popup.querySelector('.newsletter-popup__input');
    if (emailInput) {
      setTimeout(() => emailInput.focus(), 300);
    }

    // Trap focus within popup
    this.trapFocus();
  }

  close() {
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('newsletter-popup-open');

    // Save dismissal to localStorage
    this.saveDismissal();

    // Release focus trap
    this.releaseFocusTrap();
  }

  isOpen() {
    return this.popup.getAttribute('aria-hidden') === 'false';
  }

  saveDismissal() {
    const data = {
      timestamp: Date.now(),
    };
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  trapFocus() {
    const focusableElements = this.popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length === 0) return;

    this.firstFocusable = focusableElements[0];
    this.lastFocusable = focusableElements[focusableElements.length - 1];

    this.handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusable) {
          e.preventDefault();
          this.lastFocusable.focus();
        }
      } else {
        if (document.activeElement === this.lastFocusable) {
          e.preventDefault();
          this.firstFocusable.focus();
        }
      }
    };

    document.addEventListener('keydown', this.handleTabKey);
  }

  releaseFocusTrap() {
    if (this.handleTabKey) {
      document.removeEventListener('keydown', this.handleTabKey);
    }
  }
}

// --- POPUP INIT ---
function initPopup() {
  new NewsletterPopup();
}

// --- DOM READY ---
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPopup);
} else {
  initPopup();
}
